import { Dispatch, SetStateAction, useState } from "react";

export interface UsePaginatedDataResult<T> {
  data: T[];
  setData: Dispatch<SetStateAction<T[]>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  hasMore: boolean;
  setHasMore: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  togglePage: () => void;
  fetchData: (
    limit: number,
    getData: (limit: number, offset: number) => Promise<T[]>
  ) => void;
  filteredData: T[];
  setFilteredData: Dispatch<SetStateAction<T[]>>;
}

type WithId = {
  id: string | number;
};

export function usePaginatedData<
  T extends WithId
>(): UsePaginatedDataResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [filteredData, setFilteredData] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (
    limit: number,
    getData: (limit: number, offset: number) => Promise<T[]>
  ) => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * limit;
      const fetchedData = await getData(limit, offset);

      if (fetchedData.length < limit) {
        setHasMore(false);
      }

      setData((prev) => {
        const existingIds = new Set(prev.map((data) => data.id));
        const newDatas = fetchedData.filter(
          (data) => !existingIds.has(data.id)
        );
        return [...prev, ...newDatas];
      });

      setFilteredData((prev) => {
        const existingIds = new Set(prev.map((data) => data.id));
        const newDatas = fetchedData.filter(
          (data) => !existingIds.has(data.id)
        );
        return [...prev, ...newDatas];
      });
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const togglePage = () => {
    if (!loading && !error) setPage((prev) => prev + 1);
  };

  return {
    data,
    setData,
    page,
    setPage,
    hasMore,
    setHasMore,
    loading,
    setLoading,
    error,
    setError,
    togglePage,
    fetchData,
    filteredData,
    setFilteredData,
  };
}
